import React, { useEffect, useRef } from "react";
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useStore } from "../../helpers";
import _ from "lodash";
import { useWindowSize } from "react-use";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";
import { safe_get } from "../../report-visuals";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LeafletMap, LoadingState, Toggle } from "../../components";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

export const MapModal = observer((props: any) => {
    const { show, geojson, data, onClose } = props;
    const [showModal, setShowModal] = useState(show)
    const [t] = useTranslation("task-manager");
    const rootStore = useStore();
    const { taskManagerStore } = rootStore;
    const { marked_locations, enable_marking_locations } = taskManagerStore;
    const { width } = useWindowSize();
    const isMobile = width <= 766;
    const [loading, setLoading] = useState(false);

    const [delay, setDelay] = useState(false);

    useEffect(() => {
        const intr = setTimeout(() => {
            setDelay(true);
        }, 100);

        return () => clearInterval(intr);
    }, []);

    console.log(props);

    const delete_marked_location = (id) => {
        const _clone = _.clone(toJS(marked_locations));
        const index = _.findIndex(_clone, (e) => e["id"] === id);
        _clone.splice(index, 1);
        taskManagerStore.set_marked_locations(_clone);
    };

    const [active_loc, setActive_loc] = useState({});

    const [enableMapOnClickEvent, setEnableMapOnClickEvent] = useState(
        enable_marking_locations
    );

    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
    const [mapZoom, setMapZoom] = useState<number | null>(null);
    // const [mapKey, setMapKey] = useState(0); // Key to force rerender
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        taskManagerStore.set_enable_marking_locations(enableMapOnClickEvent);
    }, [enableMapOnClickEvent]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Set initial map center to first marked location if available when modal opens
    useEffect(() => {
        if (show && marked_locations && marked_locations.length > 0) {
            const firstLocation = marked_locations.find((loc: any) => loc.lat && loc.lng);
            if (firstLocation) {
                const initialCenter: [number, number] = [firstLocation.lat, firstLocation.lng];
                console.log("Setting initial map center to first marked location:", initialCenter);
                setMapCenter(initialCenter);
                setMapZoom(15);
            }
        } else if (!show) {
            // Reset when modal closes
            setMapCenter(null);
            setMapZoom(null);
        }
    }, [show, marked_locations]);

    // Nominatim (OpenStreetMap) search - Free and open-source
    const handleSearchChange = async (value: string) => {
        setSearchQuery(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!value || value.length < 2) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                // Use Nominatim (OpenStreetMap) geocoding API - free and open-source
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=5&countrycodes=in&addressdetails=1`,
                    {
                        headers: {
                            "User-Agent": "ForThePeople-App/1.0", // Required by Nominatim
                        },
                    }
                );
                const data = await response.json();

                if (Array.isArray(data) && data.length > 0) {
                    // Transform Nominatim results to match our format
                    const formattedResults = data.map((item: any) => ({
                        place_id: item.place_id,
                        description: item.display_name,
                        lat: parseFloat(item.lat),
                        lon: parseFloat(item.lon),
                        address: item.address || {},
                    }));
                    setSearchResults(formattedResults);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error("Search error:", error);
                toast.error("Failed to search location");
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 1000); // Increased debounce time to 1 second for Nominatim rate limits
    };

    // Handle location selection - Nominatim already provides coordinates
    const handleLocationSelect = (location: any) => {
        console.log("handleLocationSelect called with:", location);

        if (location && location.lat && location.lon) {
            // Leaflet uses [lat, lng] format - ensure correct order
            const lat = typeof location.lat === 'number' ? location.lat : parseFloat(location.lat);
            const lng = typeof location.lon === 'number' ? location.lon : parseFloat(location.lon);

            console.log("Parsed coordinates:", { lat, lng });

            if (!isNaN(lat) && !isNaN(lng)) {
                const newCenter: [number, number] = [lat, lng];

                // console.log("handleLocationSelect: Setting new center", newCenter, "New mapKey will be:", mapKey + 1);

                // Update center, zoom, and force rerender with new key
                setMapCenter(newCenter);
                setMapZoom(15);
                // setMapKey(prev => {
                //     console.log("mapKey changing from", prev, "to", prev + 1);
                //     return prev + 1;
                // });

                setSearchQuery("");
                setSearchResults([]);
            } else {
                console.error("Invalid coordinates after parsing:", { lat, lng });
                toast.error("Invalid coordinates");
            }
        } else {
            console.error("Invalid location data:", location);
            toast.error("Invalid location data");
        }
    };

    return (
        <>
            <Transition.Root show={show} as={Fragment}>
                <Dialog as="div" className="relative z-[30]" onClose={() => { onClose() }}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed z-[30] inset-0 overflow-y-auto">
                        <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="self-center w-full bg-white rounded-lg p-1 text-left overflow-hidden shadow-xl sm:mx-16 transform transition-all sm:my-4 sm:p-6">
                                    <i
                                        className="fa-solid fa-circle-xmark text-purple-500 cursor-pointer"
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            fontSize: "25px",
                                            zIndex: 99999,
                                        }}
                                        onClick={(e) => onClose()}
                                    />
                                    <div className="flex flex-row flex-wrap sm:flex-nowrap">
                                        {loading && <LoadingState />}
                                        <div
                                            style={{
                                                width: isMobile
                                                    ? "100%"
                                                    : width * 0.75,
                                            }}
                                            className="relative"
                                        >
                                            {/* Location Search Bar */}
                                            <div className="absolute top-4 left-4 z-[10000] w-full max-w-md">
                                                <Autocomplete
                                                    freeSolo
                                                    options={searchResults}
                                                    getOptionLabel={(
                                                        option: any
                                                    ) =>
                                                        typeof option ===
                                                            "string"
                                                            ? option
                                                            : option.description ||
                                                            ""
                                                    }
                                                    loading={isSearching}
                                                    inputValue={searchQuery}
                                                    onInputChange={(
                                                        event,
                                                        newInputValue
                                                    ) => {
                                                        handleSearchChange(
                                                            newInputValue
                                                        );
                                                    }}
                                                    onChange={(
                                                        event,
                                                        newValue: any
                                                    ) => {
                                                        console.log("Autocomplete onChange triggered:", newValue);
                                                        if (
                                                            newValue &&
                                                            typeof newValue ===
                                                            "object" &&
                                                            newValue.lat &&
                                                            newValue.lon
                                                        ) {
                                                            console.log("Calling handleLocationSelect with:", { lat: newValue.lat, lon: newValue.lon });
                                                            handleLocationSelect(
                                                                newValue
                                                            );
                                                        } else {
                                                            console.log("newValue doesn't have lat/lon:", newValue);
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Search location (e.g., Mumbai, Delhi, Bangalore)..."
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    "white",
                                                                borderRadius:
                                                                    "8px",
                                                                "& .MuiOutlinedInput-root":
                                                                {
                                                                    borderRadius:
                                                                        "8px",
                                                                    boxShadow:
                                                                        "0 2px 8px rgba(0,0,0,0.15)",
                                                                },
                                                            }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                startAdornment:
                                                                    (
                                                                        <>
                                                                            <SearchIcon
                                                                                sx={{
                                                                                    color: "#6B7280",
                                                                                    mr: 1,
                                                                                }}
                                                                            />
                                                                            {
                                                                                params
                                                                                    .InputProps
                                                                                    .startAdornment
                                                                            }
                                                                        </>
                                                                    ),
                                                                endAdornment: (
                                                                    <>
                                                                        {isSearching ? (
                                                                            <CircularProgress
                                                                                color="inherit"
                                                                                size={
                                                                                    20
                                                                                }
                                                                            />
                                                                        ) : null}
                                                                        {
                                                                            params
                                                                                .InputProps
                                                                                .endAdornment
                                                                        }
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                    renderOption={(
                                                        props,
                                                        option: any
                                                    ) => (
                                                        <li
                                                            {...props}
                                                            key={
                                                                option.place_id ||
                                                                option
                                                            }
                                                        >
                                                            <div className="flex items-start gap-2 py-1">
                                                                <i className="fas fa-map-marker-alt text-gray-400 mt-1" />
                                                                <div className="flex flex-col min-w-0">
                                                                    <span className="text-sm font-medium text-gray-900 truncate">
                                                                        {option
                                                                            .address
                                                                            ?.city ||
                                                                            option
                                                                                .address
                                                                                ?.town ||
                                                                            option
                                                                                .address
                                                                                ?.village ||
                                                                            option
                                                                                .address
                                                                                ?.state ||
                                                                            ""}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 truncate">
                                                                        {option.description ||
                                                                            option}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}
                                                />
                                            </div>
                                            {delay && (
                                                <LeafletMap
                                                    // key={`map-${mapKey}`}
                                                    geojson={geojson}
                                                    map_height={
                                                        isMobile ? 300 : 500
                                                    }
                                                    task={data}
                                                    searchCenter={mapCenter}
                                                    searchZoom={mapZoom}
                                                />
                                            )}
                                        </div>
                                        <div
                                            className="flex h-full flex-col py-6 shadow-xl bg-gray-200 rounded-lg bg-gray-300 sm:ml-2 p-2 sm:p-4"
                                            style={{
                                                width: isMobile
                                                    ? "100%"
                                                    : width * 0.25,
                                            }}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="font-bold text-xl text-blue-600">
                                                    {t(
                                                        "enable_mark_locations"
                                                    )}{" "}
                                                </p>
                                                <Toggle
                                                    val={enableMapOnClickEvent}
                                                    onToggle={() => {
                                                        setEnableMapOnClickEvent(
                                                            !enableMapOnClickEvent
                                                        );
                                                    }}
                                                />
                                            </div>
                                            <div className="font-bold text-xl text-blue-600 mt-2">
                                                {t("marked_locations")}
                                            </div>
                                            {marked_locations &&
                                                marked_locations.map(
                                                    (location) => {
                                                        return (
                                                            <>
                                                                <div className="grid grid-cols-3 gap-2 my-1">
                                                                    <p>
                                                                        {
                                                                            location.id
                                                                        }
                                                                    </p>
                                                                    <input
                                                                        onFocus={(
                                                                            e
                                                                        ) =>
                                                                            setActive_loc(
                                                                                location
                                                                            )
                                                                        }
                                                                        className="px-2 rounded-md border bg-tranaperent"
                                                                        type="number"
                                                                        defaultValue={safe_get(
                                                                            location,
                                                                            "radius",
                                                                            75
                                                                        )}
                                                                        onChange={(
                                                                            e
                                                                        ) => {
                                                                            try {
                                                                                const new_radius =
                                                                                    parseInt(
                                                                                        e
                                                                                            .target
                                                                                            .value
                                                                                    );
                                                                                if (
                                                                                    new_radius !=
                                                                                    safe_get(
                                                                                        location,
                                                                                        "radius",
                                                                                        75
                                                                                    )
                                                                                ) {
                                                                                    const loc =
                                                                                        _.clone(
                                                                                            location
                                                                                        );
                                                                                    loc[
                                                                                        "radius"
                                                                                    ] =
                                                                                        new_radius;
                                                                                    taskManagerStore.update_marked_location(
                                                                                        loc
                                                                                    );
                                                                                }
                                                                            } catch (e) { }
                                                                        }}
                                                                    />

                                                                    <div className="flex items-center justify-around">
                                                                        <i
                                                                            className="fa-solid fa-trash text-red-600 text-xl cursor-pointer"
                                                                            onClick={(
                                                                                e
                                                                            ) => {
                                                                                delete_marked_location(
                                                                                    location.id
                                                                                );
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </>
                                                        );
                                                    }
                                                )}
                                            <div className="pt-2 sm:pt-4 flex flex-row-reverse justify-start divide-y divide-gray-200">
                                                <button
                                                    className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                    onClick={async (e) => {
                                                        setLoading(true);
                                                        const res =
                                                            await taskManagerStore.save_locations();
                                                        if (
                                                            res.status === 200
                                                        ) {
                                                            toast.success(
                                                                "Locations Marked successfully."
                                                            );
                                                            await taskManagerStore.update_show_booth_map(
                                                                false
                                                            );
                                                            await taskManagerStore.set_task_view_modal(
                                                                true
                                                            );
                                                        } else {
                                                            toast.error(
                                                                "Something went wrong, please try again."
                                                            );
                                                        }
                                                        setLoading(false);
                                                    }}
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    );
});
